import React from 'react';
import { Plus, CopyPlus, Camera } from 'lucide-react';
import { photoUrl } from './PhotoAttach';
import { DAY_START, DAY_END, fromHHMM, fmtHours, iso, addDays, fmtDay, todayIso, textOn, startOfWeek } from './utils';

const PX_PER_HOUR = 44;
const HOURS = Array.from({ length: (DAY_END - DAY_START) / 60 + 1 }, (_, i) => DAY_START / 60 + i);

function Block({ e, onClick, compact }) {
  const bg = e.job_type_colour || '#475569';
  return (
    <button type="button" onClick={() => onClick(e)} data-testid={`ts-entry-${e.id}`} title={`${e.job_type_name}${e.description ? ` — ${e.description}` : ''} · ${fmtHours(e.minutes)}`}
      className="w-full text-left rounded-md px-1.5 py-1 shadow-sm hover:brightness-110 active:scale-[0.99] overflow-hidden"
      style={{ background: bg, color: textOn(bg) }}>
      <div className={`font-bold truncate flex items-center gap-1 ${compact ? 'text-[10px]' : 'text-xs'}`}>{e.photos?.length > 0 && <Camera className="w-3 h-3 shrink-0" data-testid={`ts-entry-photo-badge-${e.id}`} />}<span className="truncate">{e.description || e.job_type_name}</span></div>
      {!compact && <div className="text-[10px] opacity-90 truncate">{e.description ? `${e.job_type_name} · ` : ''}{e.start ? `${e.start}–${e.end}` : fmtHours(e.minutes)}</div>}
    </button>
  );
}

function DayColumn({ date, entries, onAdd, onOpen, showPerson }) {
  const timed = entries.filter((e) => e.start);
  const untimed = entries.filter((e) => !e.start);
  const total = entries.reduce((a, e) => a + (e.minutes || 0), 0);
  const isToday = date === todayIso();
  return (
    <div className="flex-1 min-w-[120px] border-l first:border-l-0" data-testid={`ts-day-${date}`}>
      <div className={`sticky top-0 z-10 px-2 py-1.5 border-b text-center ${isToday ? 'bg-amber-50' : 'bg-white'}`}>
        <div className={`text-xs font-bold ${isToday ? 'text-amber-800' : 'text-gray-700'}`}>{fmtDay(new Date(`${date}T00:00:00`))}</div>
        <div className="text-[11px] text-gray-500" data-testid={`ts-day-total-${date}`}>{total ? fmtHours(total) : '—'}</div>
      </div>
      {untimed.length > 0 && (
        <div className="p-1 space-y-1 border-b bg-gray-50/60">
          {untimed.map((e) => <Block key={e.id} e={e} onClick={onOpen} compact />)}
        </div>
      )}
      <div className="relative" style={{ height: (DAY_END - DAY_START) / 60 * PX_PER_HOUR }} onDoubleClick={() => onAdd(date)}>
        {HOURS.map((h) => <div key={h} className="absolute left-0 right-0 border-t border-dashed border-gray-100" style={{ top: (h * 60 - DAY_START) / 60 * PX_PER_HOUR }} />)}
        {timed.map((e) => {
          const s = Math.max(fromHHMM(e.start), DAY_START); const en = Math.min(fromHHMM(e.end), DAY_END);
          const top = (s - DAY_START) / 60 * PX_PER_HOUR; const h = Math.max(((en - s) / 60) * PX_PER_HOUR - 2, 18);
          return (
            <div key={e.id} className="absolute left-1 right-1" style={{ top, height: h }}>
              <div className="h-full [&>button]:h-full"><Block e={e} onClick={onOpen} compact={h < 34} />{showPerson && <span className="sr-only">{e.user_name}</span>}</div>
            </div>
          );
        })}
        <button type="button" onClick={() => onAdd(date)} className="absolute bottom-1 right-1 w-7 h-7 rounded-full bg-white border shadow text-gray-500 hover:text-gray-900 flex items-center justify-center" aria-label={`Add time on ${date}`} data-testid={`ts-add-${date}`}>
          <Plus className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export function WeekView({ anchor, entries, onAdd, onOpen, showPerson }) {
  const start = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => iso(addDays(start, i)));
  return (
    <div className="bg-white rounded-xl border overflow-x-auto" data-testid="ts-week-view">
      <div className="flex min-w-[860px]">
        <div className="w-12 shrink-0 border-r">
          <div className="h-[46px] border-b" />
          <div className="relative" style={{ height: (DAY_END - DAY_START) / 60 * PX_PER_HOUR }}>
            {HOURS.map((h) => <div key={h} className="absolute right-1 text-[10px] text-gray-400 -translate-y-1/2" style={{ top: (h * 60 - DAY_START) / 60 * PX_PER_HOUR }}>{String(h).padStart(2, '0')}</div>)}
          </div>
        </div>
        {days.map((d) => <DayColumn key={d} date={d} entries={entries.filter((e) => e.date === d)} onAdd={onAdd} onOpen={onOpen} showPerson={showPerson} />)}
      </div>
    </div>
  );
}

export function DayView({ anchor, entries, onAdd, onOpen, showPerson, onCopyPrev }) {
  const d = iso(anchor);
  const list = entries.filter((e) => e.date === d).sort((a, b) => (a.start || '99').localeCompare(b.start || '99'));
  const total = list.reduce((a, e) => a + (e.minutes || 0), 0);
  return (
    <div className="space-y-3" data-testid="ts-day-view">
      <div className="bg-white rounded-xl border p-3 flex items-center justify-between">
        <div><div className="font-bold">{fmtDay(anchor, { weekday: 'long', day: 'numeric', month: 'long' })}</div><div className="text-xs text-gray-500">{list.length} entr{list.length === 1 ? 'y' : 'ies'}</div></div>
        <div className="text-2xl font-black" data-testid="ts-day-view-total">{fmtHours(total)}</div>
      </div>
      {list.length === 0 && (
        <div className="text-center text-sm text-gray-500 py-8 border-2 border-dashed rounded-xl bg-white space-y-3">
          <div>Nothing logged yet — tap <strong>Log time</strong>.</div>
          {onCopyPrev && (
            <button type="button" onClick={() => onCopyPrev(d)} className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-full px-4 py-2" data-testid="ts-copy-yesterday-empty">
              <CopyPlus className="w-4 h-4" /> Same as yesterday
            </button>
          )}
        </div>
      )}
      <ul className="space-y-2">
        {list.map((e) => {
          const bg = e.job_type_colour || '#475569';
          return (
            <li key={e.id}>
              <button type="button" onClick={() => onOpen(e)} data-testid={`ts-entry-${e.id}`} className="w-full text-left bg-white rounded-xl border p-3 flex items-center gap-3 hover:shadow-md active:scale-[0.995]">
                <div className="w-1.5 self-stretch rounded-full" style={{ background: bg }} />
                <div className="w-24 shrink-0 text-sm font-mono font-bold text-gray-800">{e.start ? <>{e.start}<span className="text-gray-400">–</span>{e.end}</> : <span className="text-gray-500">{fmtHours(e.minutes)}</span>}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">{e.description || e.job_type_name}</div>
                  <div className="text-xs text-gray-500 truncate">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold mr-1" style={{ background: bg, color: textOn(bg) }}>{e.job_type_name}</span>
                    {showPerson && <span className="mr-1">{e.user_name} ·</span>}{e.link?.label ? `↗ ${e.link.label}` : ''}{e.notes ? ` · ${e.notes}` : ''}
                  </div>
                </div>
                {e.photos?.length > 0 && (
                  <div className="flex -space-x-2 shrink-0" data-testid={`ts-entry-photos-${e.id}`}>
                    {e.photos.slice(0, 3).map((id) => <img key={id} src={photoUrl(id)} alt="" className="w-9 h-9 rounded-md object-cover border-2 border-white shadow-sm" />)}
                    {e.photos.length > 3 && <span className="w-9 h-9 rounded-md bg-gray-100 border-2 border-white text-[10px] font-bold flex items-center justify-center text-gray-600">+{e.photos.length - 3}</span>}
                  </div>
                )}
                <div className="text-sm font-bold text-gray-700 shrink-0">{fmtHours(e.minutes)}</div>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2">
        <button type="button" onClick={() => onAdd(d)} className="flex-1 rounded-xl border-2 border-dashed py-3 text-sm font-semibold text-gray-600 hover:border-gray-400 bg-white" data-testid="ts-day-add"><Plus className="w-4 h-4 inline mr-1" /> Add time on this day</button>
        {onCopyPrev && <button type="button" onClick={() => onCopyPrev(d)} className="rounded-xl border-2 border-dashed px-4 py-3 text-sm font-semibold text-gray-600 hover:border-gray-400 bg-white" title="Copy yesterday's entries onto this day" data-testid="ts-copy-yesterday"><CopyPlus className="w-4 h-4 inline mr-1" /> Copy yesterday</button>}
      </div>
    </div>
  );
}

export function MonthView({ anchor, entries, onPickDay, onAdd }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = startOfWeek(first);
  const cells = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const byDay = {};
  entries.forEach((e) => { (byDay[e.date] ||= []).push(e); });
  const maxMins = Math.max(60, ...Object.values(byDay).map((l) => l.reduce((a, e) => a + e.minutes, 0)));
  return (
    <div className="bg-white rounded-xl border overflow-hidden" data-testid="ts-month-view">
      <div className="grid grid-cols-7 text-[11px] font-bold text-gray-500 border-b">
        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d) => {
          const key = iso(d); const list = byDay[key] || []; const total = list.reduce((a, e) => a + e.minutes, 0);
          const inMonth = d.getMonth() === anchor.getMonth(); const isToday = key === todayIso();
          const jobs = [...new Map(list.map((e) => [e.job_type_id, e])).values()];
          return (
            <div key={key} className={`min-h-[86px] border-b border-r p-1.5 flex flex-col ${inMonth ? '' : 'bg-gray-50/70 text-gray-400'}`} data-testid={`ts-month-day-${key}`}>
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => onPickDay(d)} className={`text-xs font-bold w-6 h-6 rounded-full ${isToday ? 'bg-amber-500 text-white' : 'hover:bg-gray-100'}`} data-testid={`ts-month-pick-${key}`}>{d.getDate()}</button>
                {total > 0 && <span className="text-[11px] font-bold text-gray-700" data-testid={`ts-month-total-${key}`}>{fmtHours(total)}</span>}
              </div>
              {total > 0 && (
                <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden flex">
                  {jobs.map((j) => {
                    const m = list.filter((e) => e.job_type_id === j.job_type_id).reduce((a, e) => a + e.minutes, 0);
                    return <div key={j.job_type_id} style={{ width: `${(m / maxMins) * 100}%`, background: j.job_type_colour }} title={`${j.job_type_name} ${fmtHours(m)}`} />;
                  })}
                </div>
              )}
              <div className="mt-1 space-y-0.5 overflow-hidden flex-1">
                {list.slice(0, 3).map((e) => (
                  <div key={e.id} className="text-[10px] truncate flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: e.job_type_colour }} />{e.description || e.job_type_name}</div>
                ))}
                {list.length > 3 && <div className="text-[10px] text-gray-400">+{list.length - 3} more</div>}
              </div>
              {inMonth && <button type="button" onClick={() => onAdd(key)} className="self-end text-gray-300 hover:text-gray-700" aria-label={`Add time on ${key}`} data-testid={`ts-add-${key}`}><Plus className="w-3.5 h-3.5" /></button>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
